/**
 * Unified MediaUploader Component
 *
 * Supports video, audio, and image uploads with drag-and-drop.
 * Consolidates: dubbing/MediaUploader, speechtotext/MediaUploader, image/ImageUploader
 */

import React, { useState, useCallback, useRef, useMemo, memo } from 'react';
import { Upload, Video, Music, ImageIcon, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { FILE_SIZE_LIMITS, formatFileSize, MB } from '@/constants/fileSize';

// ============================================================================
// Types
// ============================================================================

export type MediaType = 'video' | 'audio' | 'image';

export interface MediaUploaderProps {
  /** Which media types to accept */
  accept: MediaType[];
  /** Callback when file is selected (single-file mode) */
  onFileSelect: (file: File, mediaType: MediaType, base64?: string) => void;
  /** Callback when multiple files are selected (multiple mode) */
  onMultipleFilesSelect?: (files: Array<{ file: File; mediaType: MediaType }>) => void;
  /** Allow selecting multiple files at once */
  multiple?: boolean;
  /** Callback when file is removed */
  onRemove?: () => void;
  /** Whether upload is disabled */
  disabled?: boolean;
  /** Whether file is currently uploading */
  isUploading?: boolean;
  /** Upload progress (0-100) */
  uploadProgress?: number;
  /** Maximum file size in MB */
  maxSizeMB?: number;
  /** External preview URL (for controlled mode) */
  previewUrl?: string;
  /** Include base64 in callback (for images) */
  includeBase64?: boolean;
  /** Custom className */
  className?: string;
  /** Custom dropzone text */
  dropzoneText?: string;
  /** Translation namespace for i18n */
  translationNamespace?: string;
}

// ============================================================================
// MIME Type Mappings
// ============================================================================

const MIME_TYPES: Record<MediaType, string[]> = {
  video: [
    'video/mp4',
    'video/avi',
    'video/mov',
    'video/webm',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-matroska',
  ],
  audio: [
    'audio/mpeg',
    'audio/mp3',
    'audio/wav',
    'audio/x-wav',
    'audio/aac',
    'audio/flac',
    'audio/x-flac',
    'audio/m4a',
    'audio/x-m4a',
    'audio/ogg',
    'audio/opus',
    'audio/x-ms-wma',
    'audio/webm',
  ],
  image: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'],
};

const EXTENSIONS: Record<MediaType, string[]> = {
  video: ['MP4', 'AVI', 'MOV', 'WebM', 'MKV', 'DAV'],
  audio: ['MP3', 'WAV', 'AAC', 'FLAC', 'M4A', 'OGG', 'OPUS', 'WMA', 'WEBM'],
  image: ['PNG', 'JPG', 'JPEG', 'WEBP', 'GIF'],
};

const DEFAULT_MAX_SIZE_MB = FILE_SIZE_LIMITS.MEDIA_MAX_MB;

// ============================================================================
// Utility Functions
// ============================================================================

function detectMediaType(file: File, acceptedTypes: MediaType[]): MediaType | null {
  // Check by MIME type first
  for (const type of acceptedTypes) {
    if (MIME_TYPES[type].includes(file.type)) {
      return type;
    }
  }

  // Fallback to extension check
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext) return null;

  for (const type of acceptedTypes) {
    const extensions = EXTENSIONS[type].map((e) => e.toLowerCase());
    if (extensions.includes(ext)) {
      return type;
    }
  }

  return null;
}

import { fileToBase64 } from '@/utils/fileToBase64';

// ============================================================================
// Component
// ============================================================================

export const MediaUploader = memo(function MediaUploader({
  accept,
  onFileSelect,
  onMultipleFilesSelect,
  multiple = false,
  onRemove,
  disabled = false,
  isUploading = false,
  uploadProgress = 0,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  previewUrl: externalPreviewUrl,
  includeBase64 = false,
  className = '',
  dropzoneText,
  translationNamespace = 'common',
}: MediaUploaderProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayPreview = externalPreviewUrl || previewUrl;

  // Build accepted MIME types + extensions string (extensions cover formats like .dav without standard MIME)
  const acceptString = useMemo(() => {
    const mimeTypes = accept.flatMap((type) => MIME_TYPES[type]);
    const extensions = accept.flatMap((type) => EXTENSIONS[type].map((e) => `.${e.toLowerCase()}`));
    return [...mimeTypes, ...extensions].join(',');
  }, [accept]);

  // Build supported formats display string
  const supportedFormats = useMemo(() => {
    return accept.flatMap((type) => EXTENSIONS[type]).join(', ');
  }, [accept]);

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      const detected = detectMediaType(file, accept);
      if (!detected) {
        return t(`${translationNamespace}.mediaUploader.invalidFormat`, {
          defaultValue: 'Invalid file format. Supported: {{formats}}',
          formats: supportedFormats,
        });
      }

      // Check file size
      const maxSizeBytes = maxSizeMB * MB;
      if (file.size > maxSizeBytes) {
        return t(`${translationNamespace}.mediaUploader.fileTooLarge`, {
          defaultValue: 'File size exceeds {{maxSize}}MB limit.',
          maxSize: maxSizeMB,
        });
      }

      return null;
    },
    [accept, maxSizeMB, supportedFormats, t, translationNamespace]
  );

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setIsLoading(true);

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setIsLoading(false);
        return;
      }

      try {
        const detectedType = detectMediaType(file, accept);
        if (!detectedType) {
          throw new Error('Could not detect media type');
        }

        setSelectedFile(file);
        setMediaType(detectedType);

        // Create preview URL
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);

        // Convert to base64 if requested (for images)
        let base64: string | undefined;
        if (includeBase64 && detectedType === 'image') {
          base64 = await fileToBase64(file);
        }

        onFileSelect(file, detectedType, base64);
      } catch {
        setError(
          t(`${translationNamespace}.mediaUploader.readError`, {
            defaultValue: 'Failed to read file',
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    [accept, includeBase64, onFileSelect, t, translationNamespace, validateFile]
  );

  const handleMultipleFiles = useCallback(
    (fileList: FileList) => {
      setError(null);
      const validFiles: Array<{ file: File; mediaType: MediaType }> = [];
      const errors: string[] = [];

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const validationError = validateFile(file);
        if (validationError) {
          errors.push(`${file.name}: ${validationError}`);
          continue;
        }
        const detectedType = detectMediaType(file, accept);
        if (!detectedType) {
          errors.push(`${file.name}: Format not recognized`);
          continue;
        }
        validFiles.push({ file, mediaType: detectedType });
      }

      if (errors.length > 0) {
        setError(errors.join('\n'));
      }

      if (validFiles.length > 0) {
        onMultipleFilesSelect?.(validFiles);
      }
    },
    [accept, onMultipleFilesSelect, validateFile]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        if (multiple && onMultipleFilesSelect) {
          handleMultipleFiles(files);
        } else {
          handleFile(files[0]);
        }
      }
    },
    [disabled, handleFile, handleMultipleFiles, multiple, onMultipleFilesSelect]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        if (multiple && onMultipleFilesSelect) {
          handleMultipleFiles(e.target.files);
        } else {
          handleFile(e.target.files[0]);
        }
      }
    },
    [handleFile, handleMultipleFiles, multiple, onMultipleFilesSelect]
  );

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleRemove = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setMediaType(null);
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onRemove?.();
  }, [onRemove, previewUrl]);

  const getFileTypeDisplay = (): string => {
    if (!selectedFile) return '';
    const ext = selectedFile.name.split('.').pop()?.toUpperCase();
    return ext || mediaType?.toUpperCase() || 'FILE';
  };

  const getMediaIcon = () => {
    switch (mediaType) {
      case 'video':
        return <Video className="w-12 h-12 text-blue-500 dark:text-blue-400" />;
      case 'audio':
        return <Music className="w-12 h-12 text-purple-500 dark:text-purple-400" />;
      case 'image':
        return <ImageIcon className="w-12 h-12 text-green-500 dark:text-green-400" />;
      default:
        return <Upload className="w-12 h-12 text-gray-400" />;
    }
  };

  const getAccentColor = () => {
    if (accept.length === 1) {
      switch (accept[0]) {
        case 'video':
          return 'blue';
        case 'audio':
          return 'purple';
        case 'image':
          return 'green';
      }
    }
    return 'blue';
  };

  const accentColor = getAccentColor();

  const defaultDropzoneText = t(`${translationNamespace}.mediaUploader.dragAndDrop`, {
    defaultValue: 'Drag and drop your file here',
  });

  return (
    <div className={`w-full ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptString}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
        multiple={multiple}
      />

      {!selectedFile && !displayPreview ? (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`
            relative border-2 border-dashed rounded-lg p-8
            transition-all duration-200 cursor-pointer
            ${isDragging ? `border-${accentColor}-500 bg-${accentColor}-50 dark:bg-${accentColor}-900/20` : 'border-gray-300 dark:border-secondary-600 bg-gray-50 dark:bg-secondary-700'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : `hover:border-${accentColor}-400 hover:bg-${accentColor}-50 dark:hover:bg-${accentColor}-900/20`}
          `}
        >
          <div className="flex flex-col items-center justify-center space-y-4">
            <div
              className={`
              w-16 h-16 rounded-full flex items-center justify-center
              ${isDragging ? `bg-${accentColor}-100 dark:bg-${accentColor}-800` : 'bg-gray-100 dark:bg-secondary-600'}
            `}
            >
              <Upload
                className={`w-8 h-8 ${isDragging ? `text-${accentColor}-600` : 'text-gray-400 dark:text-gray-300'}`}
              />
            </div>

            <div className="text-center">
              <p className="text-lg font-medium text-gray-700 dark:text-gray-200">
                {dropzoneText || defaultDropzoneText}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t(`${translationNamespace}.mediaUploader.orClickToSelect`, {
                  defaultValue: 'or click to select a file',
                })}
              </p>
            </div>

            <div className="text-xs text-gray-400 dark:text-gray-500 space-y-1 text-center">
              <p>
                {t(`${translationNamespace}.mediaUploader.supportedFormats`, {
                  defaultValue: 'Supported formats: {{formats}}',
                  formats: supportedFormats,
                })}
              </p>
              <p>
                {t(`${translationNamespace}.mediaUploader.maxFileSize`, {
                  defaultValue: 'Maximum file size: {{maxSize}}MB',
                  maxSize: maxSizeMB,
                })}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-gray-300 dark:border-secondary-600 rounded-lg p-6 bg-white dark:bg-secondary-800">
          <div className="flex items-start space-x-4">
            {/* Media preview or icon */}
            <div className="flex-shrink-0">
              {mediaType === 'video' && displayPreview ? (
                <video
                  src={displayPreview}
                  className="w-32 h-24 object-cover rounded-lg"
                  controls={false}
                />
              ) : mediaType === 'image' && displayPreview ? (
                <img
                  src={displayPreview}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-lg"
                />
              ) : (
                <div
                  className={`
                  w-32 h-24 rounded-lg flex items-center justify-center
                  ${mediaType === 'audio' ? 'bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30' : 'bg-gray-100 dark:bg-secondary-700'}
                `}
                >
                  {getMediaIcon()}
                </div>
              )}
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  {selectedFile ? (
                    <>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatFileSize(selectedFile.size)} • {getFileTypeDisplay()}
                        {mediaType && (
                          <span
                            className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                              mediaType === 'audio'
                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                : mediaType === 'image'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            }`}
                          >
                            {mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}
                          </span>
                        )}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {t(`${translationNamespace}.mediaUploader.fileReady`, {
                        defaultValue: 'File ready',
                      })}
                    </p>
                  )}
                </div>

                {!isUploading && !isLoading && (
                  <button
                    onClick={handleRemove}
                    className="flex-shrink-0 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                    aria-label="Remove file"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                )}
              </div>

              {/* Loading state */}
              {isLoading && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  {t(`${translationNamespace}.mediaUploader.loading`, {
                    defaultValue: 'Loading file...',
                  })}
                </p>
              )}

              {/* Upload progress */}
              {isUploading && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>
                      {t(`${translationNamespace}.mediaUploader.uploading`, {
                        defaultValue: 'Uploading...',
                      })}
                    </span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-secondary-600 rounded-full h-2 overflow-hidden">
                    <div
                      className={`bg-${accentColor}-600 h-full transition-all duration-300`}
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Success message */}
              {uploadProgress === 100 && !isUploading && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  {t(`${translationNamespace}.mediaUploader.uploadComplete`, {
                    defaultValue: 'Upload complete',
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
    </div>
  );
});

export default MediaUploader;
